import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import type { Robot } from '../labyrinth/Robot';

type FloatingText = Robot['floatingTexts'][number];

const CANVAS_W = 256;
const CANVAS_H = 96;
const FONT_PX_NORMAL = 22;
const FONT_PX_BIG    = 32;
const Y_OFFSET = TILE_SIZE * 0.6;

interface Entry {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  texture: THREE.CanvasTexture;
  material: THREE.SpriteMaterial;
  sprite: THREE.Sprite;
  lastText: string;
  lastColor: number;
  lastBig: boolean;
}

/**
 * Screen-space floating damage numbers, matching the Pixi Text pipeline in
 * LabyrinthState.drawFloatingTexts. One THREE.Sprite per active text; canvas
 * bake only when text / color / size changes. Counter-scaled by camera zoom
 * so numbers stay screen-consistent at every distance.
 */
export class FloatingTexts3D {
  readonly group = new THREE.Group();
  private readonly entries = new Map<FloatingText, Entry>();

  sync(texts: ReadonlyArray<FloatingText>, zoom: number): void {
    const alive = new Set<FloatingText>();
    for (const ft of texts) {
      alive.add(ft);
      let entry = this.entries.get(ft);
      if (!entry) {
        entry = this.createEntry();
        this.entries.set(ft, entry);
      }
      if (entry.lastText !== ft.text || entry.lastColor !== ft.color || entry.lastBig !== ft.big) {
        this.bake(entry, ft.text, ft.color, ft.big);
      }

      const progress = Math.max(0, Math.min(1, 1 - ft.life / ft.maxLife));

      // Pop-in / grow — mirrors 2D
      let popScale: number;
      if (progress < 0.15) {
        const t = progress / 0.15;
        popScale = 1 + 0.3 * (1 - Math.pow(1 - t, 3));
      } else {
        popScale = 1.3 + (progress - 0.15) * 0.15;
      }

      // Hold then quadratic fade
      const alpha = progress < 0.4 ? 1 : 1 - Math.pow((progress - 0.4) / 0.6, 2);

      // Canvas pixel ↔ screen pixel via scale = pixels / zoom (perspective
      // cancels with our camera: dist = (screenH/2)/tan(fov/2)/zoom).
      const invZoom = 1 / Math.max(zoom, 0.001);
      entry.sprite.scale.set(CANVAS_W * invZoom * popScale, CANVAS_H * invZoom * popScale, 1);
      entry.sprite.position.set(ft.x * TILE_SIZE, Y_OFFSET, ft.y * TILE_SIZE);
      entry.material.opacity = alpha;
    }

    for (const [ft, entry] of this.entries) {
      if (alive.has(ft)) continue;
      this.group.remove(entry.sprite);
      entry.material.dispose();
      entry.texture.dispose();
      this.entries.delete(ft);
    }
  }

  private createEntry(): Entry {
    const canvas = document.createElement('canvas');
    canvas.width  = CANVAS_W;
    canvas.height = CANVAS_H;
    const ctx = canvas.getContext('2d')!;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      depthTest: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.renderOrder = 30;
    this.group.add(sprite);
    return {
      canvas, ctx, texture, material, sprite,
      lastText: '', lastColor: 0, lastBig: false,
    };
  }

  private bake(entry: Entry, text: string, color: number, big: boolean): void {
    const { ctx } = entry;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    const px = big ? FONT_PX_BIG : FONT_PX_NORMAL;
    ctx.font = `bold ${px}px Offside, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const cx = CANVAS_W / 2;
    const cy = CANVAS_H / 2;

    ctx.lineWidth = 4;
    ctx.strokeStyle = '#000000';
    ctx.strokeText(text, cx, cy);

    ctx.fillStyle = '#' + color.toString(16).padStart(6, '0');
    ctx.fillText(text, cx, cy);

    entry.texture.needsUpdate = true;
    entry.lastText = text;
    entry.lastColor = color;
    entry.lastBig = big;
  }

  dispose(): void {
    for (const entry of this.entries.values()) {
      this.group.remove(entry.sprite);
      entry.material.dispose();
      entry.texture.dispose();
    }
    this.entries.clear();
  }
}
